import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { publicApi, MenuItemData, SiteContactLinkData } from '../../api/public.api';
import { Mail, Shield, Zap, Globe, Phone } from 'lucide-react';
import BrandMark from '../ui/BrandMark';
import { useBrand } from '../../hooks/useBrand';
import { normalizeMenuUrl, resolveMenuHref } from '../../utils/menu';

function FooterLink({ item }: { item: MenuItemData }) {
  const href = resolveMenuHref(item.url);
  if (!href) {
    return <span className="text-sm text-gray-500">{item.label}</span>;
  }
  if (/^https?:\/\//i.test(href) || href.startsWith('//') || href.startsWith('mailto:') || href.startsWith('tel:')) {
    return (
      <a
        href={href}
        target={item.target === '_blank' ? '_blank' : undefined}
        rel={item.target === '_blank' ? 'noopener noreferrer' : undefined}
        className="text-sm text-gray-400 hover:text-white transition-colors duration-200"
      >
        {item.label}
      </a>
    );
  }
  return (
    <Link to={href} className="text-sm text-gray-400 hover:text-white transition-colors duration-200">
      {item.label}
    </Link>
  );
}

export default function Footer() {
  const brand = useBrand();
  const { t, i18n } = useTranslation();
  const [footerMenu, setFooterMenu] = useState<MenuItemData[]>([]);
  const [contacts, setContacts] = useState<SiteContactLinkData[]>([]);
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    const fetchFooterMenu = async () => {
      try {
        const items = await publicApi.getMenu('footer');
        setFooterMenu(Array.isArray(items) ? items : []);
      } catch {
        setFooterMenu([]);
      }
    };
    fetchFooterMenu();
  }, [i18n.language]);

  useEffect(() => {
    publicApi.getSiteContacts().then(setContacts).catch(() => setContacts([]));
  }, []);

  const networks = ['TRON', 'Ethereum', 'BSC', 'Polygon', 'Solana'];

  /** Column sections (have children) and flat links (url only) — both append without wiping fallback. */
  const sections = (footerMenu ?? []).filter((s) => (s.children && s.children.length > 0) || !normalizeMenuUrl(s.url));
  const flatLinks = (footerMenu ?? []).filter(
    (s) => normalizeMenuUrl(s.url) && (!s.children || s.children.length === 0)
  );

  return (
    <footer className="bg-gray-950 text-gray-400 border-t border-white/5 pb-[env(safe-area-inset-bottom)]">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-8 lg:py-12">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-5 lg:gap-10">
          {/* Brand Column */}
          <div className="col-span-2 sm:col-span-3 md:col-span-4 lg:col-span-2">
            <div className="mb-4 sm:mb-6">
              <BrandMark variant="logo" size="h-10" to="/" />
            </div>
            <p className="text-gray-400 text-sm leading-relaxed mb-4 sm:mb-6 max-w-sm">
              {t('footer.description')}
            </p>

            {/* Supported Networks */}
            <div className="flex flex-wrap gap-2">
              {networks.map((network) => (
                <span
                  key={network}
                  className="px-3 py-1 rounded-full bg-white/5 text-xs font-medium text-gray-400 border border-white/5"
                >
                  {network}
                </span>
              ))}
            </div>
          </div>

          {/* Dynamic footer: nested sections + flat extra links */}
          {sections.map((section) => (
            <div key={section.id}>
              <h2 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
                {section.label}
              </h2>
              {section.children && section.children.length > 0 ? (
                <ul className="space-y-3">
                  {section.children.map((item) => (
                    <li key={item.id}>
                      <FooterLink item={item} />
                    </li>
                  ))}
                </ul>
              ) : resolveMenuHref(section.url) ? (
                <ul className="space-y-3">
                  <li>
                    <FooterLink item={section} />
                  </li>
                </ul>
              ) : null}
            </div>
          ))}

          {flatLinks.length > 0 && (
            <div>
              <h2 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
                {t('footer.links', 'Links')}
              </h2>
              <ul className="space-y-3">
                {flatLinks.map((item) => (
                  <li key={item.id}>
                    <FooterLink item={item} />
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Built-in columns always remain; CMS menu adds extra columns/links */}
          {(
            <>
              <div>
                <h2 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
                  {t('footer.company')}
                </h2>
                <ul className="space-y-3">
                  <li><Link to="/about" className="text-sm text-gray-400 hover:text-white transition-colors">{t('footer.about')}</Link></li>
                  <li><Link to="/contact" className="text-sm text-gray-400 hover:text-white transition-colors">{t('footer.contact_us')}</Link></li>
                </ul>
              </div>
              <div>
                <h2 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
                  {t('footer.product')}
                </h2>
                <ul className="space-y-3">
                  <li><Link to="/features" className="text-sm text-gray-400 hover:text-white transition-colors">{t('nav.features')}</Link></li>
                  <li><Link to="/pricing" className="text-sm text-gray-400 hover:text-white transition-colors">{t('nav.pricing')}</Link></li>
                  <li><Link to="/guide" className="text-sm text-gray-400 hover:text-white transition-colors">{t('nav.guide', 'User Guide')}</Link></li>
                  <li><Link to="/documentation" className="text-sm text-gray-400 hover:text-white transition-colors">{t('nav.docs')}</Link></li>
                </ul>
              </div>
              <div>
                <h2 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
                  {t('footer.resources')}
                </h2>
                <ul className="space-y-3">
                  <li><Link to="/blog" className="text-sm text-gray-400 hover:text-white transition-colors">{t('nav.blog', 'Blog')}</Link></li>
                  <li><Link to="/apps" className="text-sm text-gray-400 hover:text-white transition-colors">{t('nav.apps', 'Apps')}</Link></li>
                  <li><Link to="/guide" className="text-sm text-gray-400 hover:text-white transition-colors">{t('nav.guide', 'User Guide')}</Link></li>
                  <li><Link to="/documentation" className="text-sm text-gray-400 hover:text-white transition-colors">{t('footer.api_reference')}</Link></li>
                  <li><Link to="/contact" className="text-sm text-gray-400 hover:text-white transition-colors">{t('footer.support')}</Link></li>
                </ul>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Features strip */}
      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 text-center sm:text-left">
            <div className="flex flex-col sm:flex-row items-center justify-center sm:justify-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                <Shield size={20} className="text-blue-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-white">{t('footer.secure_payments')}</p>
                <p className="text-xs text-gray-400">{t('footer.secure_desc')}</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-center sm:justify-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center flex-shrink-0">
                <Zap size={20} className="text-cyan-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-white">{t('footer.instant_settlement')}</p>
                <p className="text-xs text-gray-400">{t('footer.instant_desc')}</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-center sm:justify-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center flex-shrink-0">
                <Globe size={20} className="text-purple-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-white">{t('footer.global_support')}</p>
                <p className="text-xs text-gray-400">{t('footer.global_desc')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-gray-400">
              &copy; 2016 - {currentYear} {brand.fullName} — {t('common.all_rights_reserved')}
            </p>
            <div className="flex flex-wrap items-center justify-center sm:justify-end gap-4">
              {contacts.length > 0 ? (
                contacts.map((c) => (
                  <a
                    key={c.id}
                    href={c.href}
                    target={c.category === 'social' ? '_blank' : undefined}
                    rel={c.category === 'social' ? 'noopener noreferrer' : undefined}
                    title={c.label}
                    className="text-xs text-gray-400 hover:text-white transition-colors flex items-center gap-1"
                  >
                    {c.category === 'email' ? <Mail size={12} /> : c.category === 'phone' ? <Phone size={12} /> : <Globe size={12} />}
                    {c.label}
                  </a>
                ))
              ) : (
                <a href={`mailto:${brand.supportEmail}`} className="text-xs text-gray-400 hover:text-gray-300 flex items-center gap-1 transition-colors">
                  <Mail size={12} />
                  {brand.supportEmail}
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
